import {
    type FieldValue,
    type ProviderFieldSchema,
    type ProviderSchema,
    type SecretName,
    type SecretsPatch,
    secretNamesOf,
} from '@/domain/providerConfig';
import { cageIdOf, toFormKey } from '@/routes/shared/ProviderConfig/form/formKeys';
import {
    normalizeValue,
    sameValue,
    secretsPatchOf,
} from '@/routes/shared/ProviderConfig/form/formLeaves';

import type { PenFormValues, ProviderFormValues } from './formModel';

export {
    type DiffSummary,
    isEmptyDiff,
    needsConnectionTest,
    onlyDeactivatesPens,
    summarizeDiff,
} from './diffSummary';

export interface PenDiff {
    create: boolean;
    isActive?: boolean;
    remove: boolean;
    /** Schema keys; an emptied value is null. */
    fields: Record<string, FieldValue>;
    secrets: SecretsPatch;
    /**
     * The site-scope values in `fields` that move the pen off what it would hold without
     * them (R16): the new site value when this save sets one (R16a), else its stored or
     * the site's agreed value. The server signs exactly these into a test token.
     */
    connectionChanges: Record<string, FieldValue>;
}

export interface FormDiff {
    connection: Record<string, FieldValue>;
    secrets: SecretsPatch;
    pens: Record<number, PenDiff>;
}

interface Context {
    fields: ProviderFieldSchema[];
    secretNames: SecretName[];
    base: ProviderFormValues;
    current: ProviderFormValues;
    connection: Record<string, FieldValue>;
}

function changedSiteValues(
    ctx: Context,
    fields: Record<string, FieldValue>,
    baseline: (field: ProviderFieldSchema) => FieldValue
): Record<string, FieldValue> {
    const changes: Record<string, FieldValue> = {};
    for (const field of ctx.fields) {
        if (field.scope !== 'site' || !(field.key in fields)) continue;
        const before = field.key in ctx.connection ? ctx.connection[field.key] : baseline(field);
        if (!sameValue(fields[field.key], before)) changes[field.key] = fields[field.key];
    }
    return changes;
}

/**
 * What an existing pen sends for one field, or undefined for nothing. While the save sets
 * the field's site value, the server writes that value to every pen not naming its own
 * (R16a), so a pen value that differs from the new site value is sent even when the user
 * left it as it was; a blank one follows the site.
 */
function existingPenValue(
    ctx: Context,
    field: ProviderFieldSchema,
    before: PenFormValues,
    pen: PenFormValues
): FieldValue | undefined {
    const key = toFormKey(field.key);
    const value = normalizeValue(pen.fields[key]);
    if (field.scope === 'site' && field.key in ctx.connection)
        return value === null || sameValue(value, ctx.connection[field.key]) ? undefined : value;
    return sameValue(before.fields[key], pen.fields[key]) ? undefined : value;
}

function existingPenDiff(ctx: Context, before: PenFormValues, pen: PenFormValues): PenDiff | null {
    if (pen.remove)
        return { create: false, remove: true, fields: {}, secrets: {}, connectionChanges: {} };
    const fields: Record<string, FieldValue> = {};
    for (const field of ctx.fields) {
        const value = existingPenValue(ctx, field, before, pen);
        if (value !== undefined) fields[field.key] = value;
    }
    const secrets = secretsPatchOf(pen.secrets, ctx.secretNames);
    const activeChanged = pen.isActive !== before.isActive;
    if (Object.keys(fields).length === 0 && Object.keys(secrets).length === 0 && !activeChanged)
        return null;
    const connectionChanges = changedSiteValues(ctx, fields, (field) => {
        const key = toFormKey(field.key);
        return normalizeValue(key in before.fields ? before.fields[key] : ctx.base.connection[key]);
    });
    return {
        create: false,
        ...(activeChanged && { isActive: pen.isActive }),
        remove: false,
        fields,
        secrets,
        connectionChanges,
    };
}

/**
 * A created pen carries every value the form shows for it: its own, else the site's.
 * The server copies only the site values all existing rows agree on (R31), so sending
 * them keeps the new row on what the form showed while a site edit is pending or the
 * rows disagree.
 */
function newPenDiff(ctx: Context, pen: PenFormValues): PenDiff | null {
    // Connect is itself the change, so the pen's rules and secret outlook apply from the click;
    // a pen not connected is never created, whatever its boxes hold.
    if (pen.remove || !pen.connected) return null;
    const fields: Record<string, FieldValue> = {};
    for (const field of ctx.fields) {
        const key = toFormKey(field.key);
        const own = normalizeValue(pen.fields[key]);
        const value =
            own ?? (field.scope === 'site' ? normalizeValue(ctx.current.connection[key]) : null);
        if (value !== null) fields[field.key] = value;
    }
    const connectionChanges = changedSiteValues(ctx, fields, (field) =>
        normalizeValue(ctx.base.connection[toFormKey(field.key)])
    );
    return {
        create: true,
        isActive: pen.isActive,
        remove: false,
        fields,
        secrets: secretsPatchOf(pen.secrets, ctx.secretNames),
        connectionChanges,
    };
}

/** What the save changes, in schema keys, compared value by value with `base`. */
export function diffForm(
    schema: ProviderSchema,
    base: ProviderFormValues,
    current: ProviderFormValues
): FormDiff {
    const fields = schema.fields.filter((field) => field.kind !== 'secret');
    const secretNames = secretNamesOf(schema);
    const connection: Record<string, FieldValue> = {};
    for (const field of fields) {
        if (field.scope !== 'site') continue;
        const key = toFormKey(field.key);
        if (!sameValue(base.connection[key], current.connection[key]))
            connection[field.key] = normalizeValue(current.connection[key]);
    }
    const ctx: Context = { fields, secretNames, base, current, connection };
    const pens: Record<number, PenDiff> = {};
    for (const [key, pen] of Object.entries(current.pens)) {
        const before = base.pens[key];
        const diff = before?.connected ? existingPenDiff(ctx, before, pen) : newPenDiff(ctx, pen);
        if (diff !== null) pens[cageIdOf(key)] = diff;
    }
    return { connection, secrets: secretsPatchOf(current.secrets, secretNames), pens };
}
